import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "redux/store";
import {
  setOrgIdWithRelRTK,
  clearOrgIdWithRelRTK,
} from "redux/slices/orgIdWithRelRTKSlice";
import { Roles } from "common";
import env from "env";
import {
  useGetProfileQuery,
  useGetOrganisationRelationshipsQuery,
} from "redux/services/membership";
import { getOrganisationMembership } from "utils";
import { useRoles } from "hooks/useRoles";
import { GetOrganisationRelationshipsResponse } from "redux/services/membership/types";

interface UsePayrollOwnerOrgIdResult {
  orgIdWithRel: string;
  selectedOrgIdWithRelName: string;
  listOrganizationOptions: {
    value: string;
    label: string;
  }[];
  selectedOrgIdWithRelCode: string;
  dataGetOrgRel: GetOrganisationRelationshipsResponse | undefined;
  pspSPC: string;
  setOrgIdWithRel: (orgId: string) => Promise<void>;
  clearOrgIdWithRel: () => void;
}

export const useOrgIdWithRel = (): UsePayrollOwnerOrgIdResult => {
  const dispatch = useDispatch();
  const orgIdWithRel = useSelector(
    (state: RootState) => state.orgIdWithRelRTK.orgIdWithRel
  );

  const {
    isPSPMakerCheckerRole,
    isPSPMakerRole,
    isLoading: isLoadingUseRoles,
  } = useRoles();

  const { data: profileData, isFetching: isFetchingGetProfile } =
    useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );

  const { data: dataGetOrgRel, isFetching: isFetchingGetOrgRel } =
    useGetOrganisationRelationshipsQuery(
      {
        executorOrgId: membershipOrganisation?.organisationId ?? "",
        process: env.api.approvalCategoryCode,
        role: Roles.BusinessOwner,
      },
      {
        skip: !isPSPMakerCheckerRole,
      }
    );

  const selectedOrgIdWithRelName = useMemo(() => {
    if (!isPSPMakerCheckerRole) return "";

    const selectedOrg = dataGetOrgRel?.data?.find(
      (item) => item.ownerOrg.id === orgIdWithRel
    );

    return selectedOrg?.ownerOrg.companyName ?? "";
  }, [dataGetOrgRel?.data, isPSPMakerCheckerRole, orgIdWithRel]);

  const selectedOrgIdWithRelCode = useMemo(() => {
    if (!isPSPMakerCheckerRole) return "";

    const selectedOrg = dataGetOrgRel?.data?.find(
      (item) => item.ownerOrg.id === orgIdWithRel
    );

    return selectedOrg?.ownerOrg.leiNumber ?? "";
  }, [dataGetOrgRel?.data, isPSPMakerCheckerRole, orgIdWithRel]);

  const listOrganizationOptions = useMemo(
    () =>
      dataGetOrgRel?.data?.map((item) => ({
        value: item?.ownerOrg.id,
        label: item?.ownerOrg.companyName,
      })) ?? [],
    [dataGetOrgRel]
  );

  const pspSPC = useMemo(() => {
    if (!isPSPMakerRole) return "";

    const selectedOrg = dataGetOrgRel?.data?.find(
      (item) => item.ownerOrg.id === orgIdWithRel
    );

    return selectedOrg?.ownerOrg.companyNumber ?? "";
  }, [dataGetOrgRel?.data, isPSPMakerRole, orgIdWithRel]);

  useEffect(() => {
    if (isFetchingGetOrgRel || isFetchingGetProfile || isLoadingUseRoles)
      return;

    const current = membershipOrganisation?.organisationId ?? "";

    if (!isPSPMakerCheckerRole && current && current !== orgIdWithRel) {
      dispatch(setOrgIdWithRelRTK({ orgIdWithRel: current }));
    }
  }, [
    dispatch,
    isPSPMakerCheckerRole,
    membershipOrganisation?.organisationId,
    orgIdWithRel,
    isFetchingGetOrgRel,
    isFetchingGetProfile,
    isLoadingUseRoles,
  ]);

  const setOrgIdWithRel = (newOrgId: string): Promise<void> => {
    dispatch(setOrgIdWithRelRTK({ orgIdWithRel: newOrgId }));
    return Promise.resolve();
  };

  const clearOrgIdWithRel = (): void => {
    dispatch(clearOrgIdWithRelRTK());
  };

  return {
    orgIdWithRel,
    setOrgIdWithRel,
    clearOrgIdWithRel,
    selectedOrgIdWithRelName,
    listOrganizationOptions,
    selectedOrgIdWithRelCode,
    dataGetOrgRel,
    pspSPC,
  };
};
